import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CustomerNav from "@/components/customer/CustomerNav";
import Footer from "@/components/Footer";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ADDRESS_SUGGESTIONS } from "@/lib/constants";
import { toast } from "sonner";
import { Users, Briefcase, Check, ArrowRight, MapPin, Clock as ClockIcon, ArrowUpDown } from "lucide-react";
import RouteMap from "@/components/RouteMap";
import PaymentSelector from "@/components/PaymentSelector";
import FlightLookup from "@/components/FlightLookup";
import DateTimePicker from "@/components/DateTimePicker";
import PlacesInput from "@/components/PlacesInput";
import { CouponPicker } from "@/components/Coupons";
import DispatcherAddCard from "@/components/DispatcherAddCard";

const todayIsoLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  d.setHours(d.getHours() + 2);
  return d.toISOString().slice(0, 16);
};

export default function Book() {
  const nav = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(1); // 1: trip, 2: vehicle, 3: contact
  // Mobile: whenever the booking form is shown (route opened / back to step 1) jump to
  // the top so the entry fields are visible immediately — never make the rider scroll.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [step]);
  const [tripType, setTripType] = useState("point_to_point");
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [pickupDt, setPickupDt] = useState(todayIsoLocal());
  const [hours, setHours] = useState(3);
  const [pax, setPax] = useState(1);
  const [luggage, setLuggage] = useState(1);
  const [stops, setStops] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  // Corporate billing: members choose per trip — personal or business account
  const [corpAccounts, setCorpAccounts] = useState([]);
  const [billTo, setBillTo] = useState("personal"); // "personal" | corp account_id
  useEffect(() => {
    if (!user) { setCorpAccounts([]); return; }
    api.get("/corporate/me").then(r => setCorpAccounts(r.data || [])).catch(() => {});
  }, [user]);
  // Synthetic placeholder emails (created server-side for phone-only customers
  // saved by a dispatcher) must never leak into the form.
  const _realEmail = (e) => {
    if (!e) return "";
    if (e.endsWith("@no-email.deluxlimo")) return "";
    if (/^phone\+[a-z0-9]+@no-email\./i.test(e)) return "";
    return e;
  };
  const [name, setName] = useState(user?.role === "customer" ? (user?.name || "") : "");
  const [email, setEmail] = useState(user?.role === "customer" ? _realEmail(user?.email) : "");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  // "Booking for someone else?" — guest rider info (shown on tracking + driver pages)
  const [forSomeoneElse, setForSomeoneElse] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestCount, setGuestCount] = useState("");
  const [flightData, setFlightData] = useState(null);
  const [recurring, setRecurring] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [occurrences, setOccurrences] = useState(4);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  // `true` when the customer chose "Reserve & Pay Now" — drives the Step 4 UX
  // to show the PaymentSelector as the primary CTA instead of treating the
  // confirmation as a "pay later" success page.
  const [payNowFlow, setPayNowFlow] = useState(false);
  // Dispatch-only price-match: custom total that overrides the computed fare
  const [fareAdjust, setFareAdjust] = useState("");
  const [fareAdjustReason, setFareAdjustReason] = useState("");
  const fareAdjustActive = user?.role === "dispatcher" && parseFloat(fareAdjust) > 0;
  const [tipPct, setTipPct] = useState(0.20); // 0.15 | 0.20 (default) | 0.30 | custom
  const [customTipAmt, setCustomTipAmt] = useState(""); // dollar amount string for "Custom"
  const [tipMode, setTipMode] = useState("pct"); // "pct" | "custom"
  const [cancelProtect, setCancelProtect] = useState(true);   // always-on (matches deluxtransgroup.com)
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [returnDt, setReturnDt] = useState("");
  // Exact return-leg pricing for Step 3 (reverse-route quote — mirrors backend
  // return-leg creation: no coupon, no meet & greet). Lets the review Total
  // show the true combined round-trip amount instead of the one-way fare.
  const [returnQuote, setReturnQuote] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [meetGreet, setMeetGreet] = useState(false);

  // Deep-link: /book?ref=JOHN-AB12 pre-fills the coupon code, or
  // /book?for_email=...&for_name=...&for_phone=... pre-fills the customer (dispatcher creating reservation)
  // /book?rebook=1&pickup=...&dropoff=...&trip_type=... pre-fills a repeat trip ("Book again")
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setCouponCode(ref.toUpperCase());
    const forName = params.get("for_name");
    const forEmail = params.get("for_email");
    const forPhone = params.get("for_phone");
    if (forName) setName(forName);
    if (forEmail) setEmail(forEmail);
    if (forPhone) setPhone(forPhone);
    const forPickup = params.get("for_pickup");
    if (forPickup) setPickup(forPickup);
    if (params.get("rebook") === "1") {
      const tt = params.get("trip_type");
      if (tt) setTripType(tt);
      if (params.get("pickup")) setPickup(params.get("pickup"));
      if (params.get("dropoff")) setDropoff(params.get("dropoff"));
      const px = parseInt(params.get("pax"), 10);
      if (px > 0) setPax(px);
      const lg = parseInt(params.get("luggage"), 10);
      if (lg >= 0 && !Number.isNaN(lg)) setLuggage(lg);
      const hrs = parseFloat(params.get("hours"));
      if (hrs > 0) setHours(hrs);
    }
  }, []);

  useEffect(() => {
    // Skip auto-prefill from the logged-in user when the URL is explicitly
    // pre-filling someone else (e.g. dispatcher creating a reservation FOR a customer).
    const params = new URLSearchParams(window.location.search);
    if (params.get("for_email") || params.get("for_name")) return;
    // Only auto-fill from a logged-in CUSTOMER. Dispatchers/drivers using
    // /book are creating reservations on behalf of someone else — pre-filling
    // their own identity would silently overwrite the customer's contact info.
    if (user && user.role === "customer") {
      setName(user.name || "");
      setEmail(_realEmail(user.email));
    }
  }, [user]);

  // Browser BACK button → step back through the form instead of leaving /book.
  // We push a sentinel history entry whenever we advance to step 2 or 3, and
  // intercept the popstate that follows to re-render the previous step.
  useEffect(() => {
    if (step > 1 && step < 4) {
      window.history.pushState({ book_step: step }, "");
    }
  }, [step]);

  useEffect(() => {
    const onPop = () => {
      // If a future step is on screen, fall back to the previous one and
      // re-push a sentinel so the next press behaves the same. Step 1 / 4
      // fall through to the browser's normal back behaviour.
      setStep(prev => {
        if (prev > 1 && prev < 4) {
          window.history.pushState({ book_step: prev - 1 }, "");
          return prev - 1;
        }
        return prev;
      });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);


  const canQuote = useMemo(() => {
    if (!pickup) return false;
    if (tripType === "point_to_point" && !dropoff) return false;
    if (tripType === "hourly" && (!hours || hours < 1)) return false;
    if (!pickupDt) return false;
    return true;
  }, [pickup, dropoff, tripType, hours, pickupDt]);

  // Compute effective tip percentage. If "custom" mode with dollar amount, derive percentage from the
  // currently-selected vehicle's subtotal (or first vehicle in quote if none selected yet).
  const subtotalForTip = () => {
    const v = selectedVehicle || quote?.vehicles?.[0];
    return v?.breakdown?.subtotal || 0;
  };
  const effectiveTipPct = () => {
    if (tipMode === "custom") {
      const amt = parseFloat(customTipAmt);
      const sub = subtotalForTip();
      if (!amt || !sub) return 0;
      return Math.max(0, Math.min(amt / sub, 1));
    }
    return tipPct;
  };
  // Display total for a vehicle quote given current tip choice.
  // Mirrors backend formula EXACTLY (using post-coupon subtotal so any discount
  // actually reduces the total):
  //   pre_tax = subtotal_after_coupon + tip + airport + cc_fee(3.5%) + flex_cancel($8) + tolls
  //   total   = pre_tax × 1.055
  const displayFare = (v) => {
    if (!v?.breakdown) return v?.fare || 0;
    const bd = v.breakdown;
    const subtotal = bd.subtotal_after_coupon ?? bd.subtotal ?? 0;
    const tip = subtotal * effectiveTipPct();
    const airport = bd.airport_fee || 0;
    const tolls = bd.tolls || 0;
    const meetGreetFee = bd.meet_and_greet_fee || 0;
    const ccFee = +(subtotal * 0.035).toFixed(2);
    const flex = 8.0;
    const preTax = subtotal + tip + airport + ccFee + flex + tolls + meetGreetFee;
    const tax = +(preTax * (bd.tax_pct || 0.055)).toFixed(2);
    return preTax + tax;
  };
  const tipAmount = (v) => {
    const bd = v?.breakdown || {};
    const sub = bd.subtotal_after_coupon ?? bd.subtotal ?? 0;
    return sub * effectiveTipPct();
  };

  const getQuote = async (overrideCancel, overrides = {}) => {
    // Defensive: only accept an explicit boolean as override; ignore stray React events.
    const cancelOverride = typeof overrideCancel === "boolean" ? overrideCancel : undefined;
    if (!pickup) { toast.error("Pickup address required"); return; }
    if (tripType === "point_to_point" && !dropoff) { toast.error("Drop-off address required"); return; }
    if (tripType === "hourly" && (!hours || Number(hours) < 1)) { toast.error("Hours required for hourly charter (min 1)"); return; }
    if (!pickupDt) { toast.error("Pickup date & time required"); return; }
    setQuoting(true);
    try {
      const cleanStops = stops.filter(s => s && s.trim());
      // Overrides let callers pass a freshly-updated value (e.g. new coupon code)
      // BEFORE React re-renders — avoids the stale-state closure where a coupon
      // was chosen but /quote fires with the old empty coupon_code.
      const effectiveCoupon = overrides.coupon_code !== undefined ? overrides.coupon_code : (couponCode || null);
      const effectiveMeetGreet = overrides.meet_and_greet !== undefined ? overrides.meet_and_greet : meetGreet;
      const r = await api.post(cleanStops.length ? "/quote/multistop" : "/quote", {
        trip_type: tripType,
        pickup_address: pickup,
        dropoff_address: tripType === "point_to_point" ? dropoff : null,
        hours: tripType === "hourly" ? Number(hours) : null,
        pax: Number(pax),
        luggage: Number(luggage),
        stops: cleanStops,
        tip_pct: tipPct,
        cancellation_protection: cancelOverride !== undefined ? cancelOverride : cancelProtect,
        coupon_code: effectiveCoupon,
        customer_email: email || null,
        meet_and_greet: effectiveMeetGreet,
        pickup_datetime: pickupDt || null,
      });
      setQuote(r.data);
      // CRITICAL: refresh the selected vehicle from the NEW quote response.
      // selectedVehicle is a snapshot taken on step 2 — without this, coupon /
      // meet-&-greet re-quotes update `quote` but step 3 keeps rendering the
      // stale pre-discount breakdown (the "price didn't change" bug).
      setSelectedVehicle(prev => {
        if (!prev) return prev;
        const fresh = (r.data.vehicles || []).find(v => v.vehicle_id === prev.vehicle_id);
        return fresh || prev;
      });
      // Only advance to Step 2 when the user is coming from Step 1's "Get Quote"
      // button. Inline re-quotes on later steps (meet-&-greet toggle, coupon
      // apply, tip change) must NOT force the wizard backwards.
      if (step === 1) setStep(2);
    } catch (e) {
      // Surface real error to console + toast for fast diagnosis
      // eslint-disable-next-line no-console
      console.error("getQuote error", {
        message: e?.message,
        code: e?.code,
        status: e?.response?.status,
        detail: e?.response?.data?.detail,
        url: e?.config?.url,
        baseURL: e?.config?.baseURL,
      });
      const detail = e?.response?.data?.detail;
      const msg = detail || e?.message || "Network error";
      toast.error(`Quote failed: ${msg}`);
    } finally {
      setQuoting(false);
    }
  };

  // Round trip: price the return leg (reverse route) so Step 3 shows the exact
  // combined total. Mirrors backend return-leg creation — same tip/protection,
  // NO coupon and NO meet & greet on the return ride.
  useEffect(() => {
    const active = isRoundTrip && tripType === "point_to_point" && !!returnDt && pickup && dropoff && quote;
    if (!active) { setReturnQuote(null); return; }
    let stale = false;
    api.post("/quote", {
      trip_type: "point_to_point",
      pickup_address: dropoff,
      dropoff_address: pickup,
      hours: null,
      pax: Number(pax),
      luggage: Number(luggage),
      stops: [],
      tip_pct: tipPct,
      cancellation_protection: cancelProtect,
      coupon_code: null,
      meet_and_greet: false,
      pickup_datetime: returnDt || null,
    }).then(r => { if (!stale) setReturnQuote(r.data); })
      .catch(() => { if (!stale) setReturnQuote(null); });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoundTrip, returnDt, tripType, pickup, dropoff, quote, cancelProtect, tipPct, pax, luggage]);

  // Re-fetch quote when cancellation protection toggles (it affects the breakdown line items)
  const toggleCancelProtect = (v) => {
    setCancelProtect(v);
    if (quote) {
      // Refetch so the backend recomputes fees + tax + total line items
      setTimeout(() => getQuote(v), 50);
    }
  };

  const submitBooking = async () => {
    if (!selectedVehicle) {
      toast.error("Select a vehicle");
      return;
    }
    if (!name) {
      toast.error("Please enter your name.");
      return;
    }
    if (!email && !phone) {
      toast.error("Add an email, a phone, or both — we need at least one way to reach you.");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = recurring ? "/bookings/recurring" : "/bookings";
      const body = {
        trip_type: tripType,
        pickup_address: pickup,
        dropoff_address: tripType === "point_to_point" ? dropoff : null,
        pickup_datetime: pickupDt,
        hours: tripType === "hourly" ? Number(hours) : null,
        pax: Number(pax),
        luggage: Number(luggage),
        vehicle_id: selectedVehicle.vehicle_id,
        customer_name: name,
        customer_email: (email || "").trim() || null,
        customer_phone: (phone || "").trim() || null,
        notes,
        flight_number: flightNumber || null,
        stops: stops.filter(s => s && s.trim()),
        tip_pct: effectiveTipPct(),
        cancellation_protection: cancelProtect,
        coupon_code: couponCode || null,
        is_round_trip: isRoundTrip && tripType === "point_to_point" && !!returnDt,
        return_pickup_datetime: isRoundTrip && tripType === "point_to_point" ? returnDt : null,
        meet_and_greet: meetGreet,
        guest_name: forSomeoneElse ? guestName.trim() || null : null,
        guest_phone: forSomeoneElse ? guestPhone.trim() || null : null,
        guest_count: forSomeoneElse ? Number(guestCount) || null : null,
      };
      if (corpAccounts.length > 0) {
        body.billing_choice = billTo === "personal" ? "personal" : "corporate";
        body.corporate_account_id = billTo === "personal" ? null : billTo;
      }
      if (fareAdjustActive) {
        body.fare_override = parseFloat(fareAdjust);
        if (fareAdjustReason.trim()) body.fare_override_reason = fareAdjustReason.trim();
      }
      if (recurring) {
        body.frequency = frequency;
        body.occurrences = Number(occurrences);
      }
      const r = await api.post(endpoint, body);
      if (recurring) {
        // Reshape: first booking has all the per-ride fields, plus add series metadata
        const first = r.data.bookings[0];
        setConfirmation({
          ...first,
          series_id: r.data.series_id,
          occurrences: r.data.occurrences,
          frequency: r.data.frequency,
          total_series: r.data.total_series,
          first_pickup: r.data.first_pickup,
          last_pickup: r.data.last_pickup,
        });
      } else {
        setConfirmation(r.data);
      }
      setStep(4);
      toast.success(recurring ? `${r.data.occurrences} reservations confirmed` : "Reservation confirmed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-page" className={`public-light bg-[#050505] min-h-screen text-[#F5F2EB] ${user?.role === "customer" ? "pb-20 md:pb-0" : ""}`}>
      <Navbar/>
      {user?.role === "customer" && <CustomerNav mobileOnly/>}
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="overline mb-3">Reservation</div>
          <h1 className="font-[Outfit] text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light leading-none mb-4">
            Book your <span className="italic font-normal">ride.</span>
          </h1>
          <p className="text-[#A1A1AA] max-w-xl mb-12">Flat-rate pricing in minutes · Choose your gratuity · No payment required to confirm.</p>

          <Stepper step={step} onJump={n => setStep(n)}/>
        </div>
      </section>

      <section className="pb-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto bg-[#0A0A0A] border border-white/10 p-8 md:p-12">

          {/* STEP 1 — Trip Details */}
          {step === 1 && (
            <div data-testid="step-trip">
              <div className="flex border border-white/10 w-fit mb-10">
                <button
                  data-testid="trip-type-p2p"
                  onClick={() => setTripType("point_to_point")}
                  className={`px-5 py-3 text-[11px] uppercase tracking-[0.2em] transition ${tripType==='point_to_point'?'bg-[#D4AF37] text-black':'text-[#A1A1AA] hover:text-white'}`}
                >Point to Point</button>
                <button
                  data-testid="trip-type-hourly"
                  onClick={() => setTripType("hourly")}
                  className={`px-5 py-3 text-[11px] uppercase tracking-[0.2em] transition ${tripType==='hourly'?'bg-[#D4AF37] text-black':'text-[#A1A1AA] hover:text-white'}`}
                >Hourly Charter</button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
                {/* Route list: Pickup → stops → Drop-off. Every field has ↑ ↓ ×
                    so the dispatcher can reorder any point or clear it. */}
                {(() => {
                  const p2p = tripType === "point_to_point";
                  const route = p2p ? [pickup, ...stops, dropoff] : [pickup];
                  const writeRoute = (arr) => {
                    setPickup(arr[0] || "");
                    if (p2p) { setDropoff(arr[arr.length - 1] || ""); setStops(arr.slice(1, -1)); }
                  };
                  const move = (i, dir) => {
                    const j = i + dir;
                    if (j < 0 || j >= route.length) return;
                    const next = [...route];
                    [next[i], next[j]] = [next[j], next[i]];
                    writeRoute(next);
                  };
                  const controls = (i, { onClear, onRemove, clearId, upId, downId, canMove = true }) => (
                    <div className="flex items-center gap-1 pb-1 shrink-0">
                      <button type="button" data-testid={upId} disabled={!canMove || i === 0} onClick={() => move(i, -1)} title="Move up"
                        className="px-2 py-2 border border-[#A1A1AA]/30 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">↑</button>
                      <button type="button" data-testid={downId} disabled={!canMove || i === route.length - 1} onClick={() => move(i, 1)} title="Move down"
                        className="px-2 py-2 border border-[#A1A1AA]/30 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">↓</button>
                      <button type="button" data-testid={clearId} onClick={onRemove || onClear} title={onRemove ? "Remove stop" : "Clear"}
                        className="px-2 py-2 border border-[#A1A1AA]/30 text-[#A1A1AA] hover:text-red-400 hover:border-red-400/60 transition-colors">×</button>
                    </div>
                  );
                  return (
                    <>
                      <div className="md:col-span-2 flex gap-2 items-end" data-testid="route-row-pickup">
                        <div className="flex-1 min-w-0">
                          <label className="field-label">Pickup address</label>
                          <PlacesInput testId="input-pickup" placeholder="MKE Airport, hotel, or address" value={pickup} onChange={setPickup} autoFocus/>
                        </div>
                        {controls(0, { onClear: () => setPickup(""), clearId: "clear-pickup", upId: "pickup-up", downId: "pickup-down", canMove: p2p })}
                      </div>

                      {p2p && stops.map((st, i) => (
                        <div key={i} className="md:col-span-2 flex gap-2 items-end" data-testid={`stop-row-${i}`}>
                          <div className="flex-1 min-w-0">
                            <label className="field-label">Stop {i + 1} (+$15)</label>
                            <PlacesInput testId={`input-stop-${i}`} placeholder="Intermediate address" value={st}
                              onChange={(val) => { const next = [...stops]; next[i] = val; setStops(next); }}/>
                          </div>
                          {controls(i + 1, { onRemove: () => setStops(stops.filter((_, j) => j !== i)), clearId: `remove-stop-${i}`, upId: `stop-up-${i}`, downId: `stop-down-${i}` })}
                        </div>
                      ))}

                      {p2p && (
                        <div className="md:col-span-2 flex gap-2 items-end relative" data-testid="route-row-dropoff">
                          <div className="flex-1 min-w-0">
                            <label className="field-label">Drop-off address</label>
                            <PlacesInput testId="input-dropoff" placeholder="Destination" value={dropoff} onChange={setDropoff}/>
                          </div>
                          <button
                            type="button"
                            data-testid="swap-addresses-btn"
                            onClick={() => { const p = pickup; setPickup(dropoff); setDropoff(p); }}
                            disabled={!pickup && !dropoff}
                            title="Swap pickup and drop-off"
                            aria-label="Swap pickup and drop-off addresses"
                            className="px-2 py-2 mb-1 border border-[#A1A1AA]/30 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0"
                          ><ArrowUpDown size={14}/></button>
                          {controls(route.length - 1, { onClear: () => setDropoff(""), clearId: "clear-dropoff", upId: "dropoff-up", downId: "dropoff-down" })}
                        </div>
                      )}
                    </>
                  );
                })()}

                {tripType === "point_to_point" && stops.length < 4 && (
                  <div className="md:col-span-2">
                    <button
                      type="button"
                      data-testid="add-stop-btn"
                      onClick={() => setStops([...stops, ""])}
                      className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#C29B2E]"
                    >+ Add another stop</button>
                  </div>
                )}

                <datalist id="addr-list">
                  {ADDRESS_SUGGESTIONS.map(a => <option key={a} value={a}/>)}
                </datalist>

                <div>
                  <label className="field-label">Pickup date & time</label>
                  <DateTimePicker value={pickupDt} onChange={setPickupDt} testId="input-pickup-datetime"/>
                </div>

                {tripType === "point_to_point" && (
                  <div className="border border-white/10 p-4 bg-[#0A0A0A]" data-testid="round-trip-section">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="round-trip-toggle"
                        checked={isRoundTrip}
                        onChange={e => setIsRoundTrip(e.target.checked)}
                        className="accent-[#D4AF37]"
                      />
                      <span className="text-sm text-[#F5F2EB]">Round trip / Return ride</span>
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Save on a 2nd booking call</span>
                    </label>
                    {isRoundTrip && (
                      <div className="mt-4">
                        <label className="field-label">Return pickup date &amp; time</label>
                        <DateTimePicker value={returnDt} onChange={setReturnDt} testId="input-return-datetime"/>
                        <p className="text-[10px] text-[#71717A] mt-2 uppercase tracking-[0.18em]">
                          We&apos;ll swap pickup ↔ drop-off automatically and book both legs.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {tripType === "hourly" && (
                  <div>
                    <label className="field-label">Hours (3hr min · Sprinter 4hr min)</label>
                    <input
                      data-testid="input-hours"
                      type="number"
                      min={1}
                      max={12}
                      step={0.5}
                      className="field-input"
                      value={hours}
                      onChange={e => setHours(e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <label className="field-label">Passengers</label>
                  <input data-testid="input-pax" type="number" min={1} max={14} className="field-input" value={pax} onChange={e => setPax(e.target.value)}/>
                </div>

                <div>
                  <label className="field-label">Luggage</label>
                  <input data-testid="input-luggage" type="number" min={0} max={10} className="field-input" value={luggage} onChange={e => setLuggage(e.target.value)}/>
                </div>
              </div>

              {/* Flight lookup — show if pickup or dropoff looks like an airport */}
              {(/(airport|mke|ord|mdw|msn|terminal)/i.test(pickup + " " + dropoff)) && (
                <div className="mt-8 pt-8 border-t border-white/5">
                  <FlightLookup
                    value={flightNumber}
                    onChange={setFlightNumber}
                    onResult={setFlightData}
                    pickupDatetime={pickupDt}
                  />
                </div>
              )}

              <div className="mt-12 flex flex-wrap items-center gap-4 justify-between">
                <button data-testid="back-home" className="btn-ghost" onClick={() => nav("/")}>Cancel</button>
                <button
                  data-testid="get-quote-button"
                  onClick={() => getQuote()}
                  disabled={!canQuote || quoting}
                  className="btn-gold"
                >
                  {quoting ? "Calculating…" : <>Continue <ArrowRight size={16}/></>}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Vehicle Selection */}
          {step === 2 && quote && (
            <div data-testid="step-vehicle">
              {quote.trip_type === "point_to_point" && (
                <div className="mb-6">
                  <RouteMap key={`${quote.pickup_address}|${(quote.stops || []).join("|")}|${quote.dropoff_address}`}
                    pickup={quote.pickup_address} stops={quote.stops || []} dropoff={quote.dropoff_address}
                    routePolyline={quote.route_polyline || []} height={240}/>
                </div>
              )}
              <div className="mb-8 pb-6 border-b border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Trip summary</div>
                  <button data-testid="edit-trip-from-vehicle" type="button" onClick={() => setStep(1)}
                    className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white">
                    ✎ Edit pickup / drop-off
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="flex items-center gap-2 text-[#D4AF37] mb-1"><MapPin size={14}/>Pickup</div>
                    <div className="text-[#F5F2EB]">{quote.pickup_address}</div>
                  </div>
                  {quote.trip_type === "point_to_point" ? (
                    <>
                      <div>
                        <div className="flex items-center gap-2 text-[#D4AF37] mb-1"><MapPin size={14}/>Drop-off</div>
                        <div className="text-[#F5F2EB]">{quote.dropoff_address}</div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 text-[#D4AF37] mb-1"><ClockIcon size={14}/>Distance</div>
                        <div className="text-[#F5F2EB]" data-testid="summary-distance">{quote.distance_miles} mi · {quote.duration_minutes} min</div>
                        {quote.stops?.length > 0 && (
                          <div className="text-[11px] text-[#A1A1AA] mt-1" data-testid="summary-distance-note">
                            Full route via {quote.stops.length} stop{quote.stops.length > 1 ? "s" : ""}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 text-[#D4AF37] mb-1"><ClockIcon size={14}/>Hourly</div>
                      <div className="text-[#F5F2EB]">{quote.hours} hours</div>
                    </div>
                  )}
                </div>
                {quote.trip_type === "point_to_point" && quote.stops?.length > 0 && (
                  <div className="mt-4 space-y-1.5" data-testid="summary-stops">
                    {quote.stops.map((st, i) => {
                      const leg = quote.legs?.[i];
                      return (
                        <div key={i} className="flex items-start gap-2 text-sm" data-testid={`summary-stop-${i + 1}`}>
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#D4AF37] text-[#050505] text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                          <span className="text-[#F5F2EB] flex-1">{st}</span>
                          {leg && <span className="text-[11px] text-[#A1A1AA] shrink-0">{leg.miles} mi · {leg.minutes} min to here</span>}
                        </div>
                      );
                    })}
                    {quote.legs?.length > 1 && (
                      <div className="flex items-start gap-2 text-sm">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#D4AF37] text-[#D4AF37] text-[10px] font-bold shrink-0 mt-0.5">B</span>
                        <span className="text-[#A1A1AA] flex-1">Drop-off</span>
                        <span className="text-[11px] text-[#A1A1AA] shrink-0">{quote.legs[quote.legs.length - 1].miles} mi · {quote.legs[quote.legs.length - 1].minutes} min</span>
                      </div>
                    )}
                  </div>
                )}
                {quote.source?.includes("mocked") && (
                  <div className="mt-3 text-[10px] tracking-[0.2em] uppercase text-[#71717A]">
                    Distance: MOCKED (Google Distance Matrix not configured)
                  </div>
                )}
              </div>

              {/* Tip selector */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-3">Driver Gratuity</div>
                <div className="flex flex-wrap gap-2 items-center">
                  {[0.15, 0.20, 0.30].map(p => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`tip-${Math.round(p*100)}`}
                      onClick={() => { setTipMode("pct"); setTipPct(p); }}
                      className={`px-5 py-2 text-sm border transition-colors ${tipMode==="pct" && tipPct===p ? "bg-[#D4AF37] text-[#0a0a0a] border-[#D4AF37]" : "border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/50"}`}
                    >
                      {Math.round(p*100)}%{p===0.20 && <span className="ml-1 text-[10px] opacity-70">(default)</span>}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="tip-custom"
                    onClick={() => setTipMode("custom")}
                    className={`px-5 py-2 text-sm border transition-colors ${tipMode==="custom" ? "bg-[#D4AF37] text-[#0a0a0a] border-[#D4AF37]" : "border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/50"}`}
                  >
                    Custom $
                  </button>
                  {tipMode === "custom" && (
                    <div className="flex items-center gap-2 ml-1">
                      <span className="text-[#A1A1AA]">$</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        data-testid="tip-custom-input"
                        value={customTipAmt}
                        onChange={e => setCustomTipAmt(e.target.value)}
                        className="bg-transparent border border-white/10 px-3 py-2 text-sm w-24 text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
                        placeholder="0"
                      />
                    </div>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#71717A] mt-2">
                  Tip goes 100% to your driver. You can adjust or remove at checkout.
                </div>
              </div>

              {/* Cancellation Protection */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <div className="text-sm uppercase tracking-[0.2em] text-[#F5F2EB] flex items-center gap-2">
                  Flexible Cancellation Included
                  <span className="text-[#D4AF37] text-xs">$8.00</span>
                </div>
                <div className="text-xs text-[#71717A] mt-1">
                  Every booking includes free cancellation up to 1 hour before pickup.
                </div>
              </div>

              <div className="space-y-4">
                {quote.vehicles.map(v => {
                  const sel = selectedVehicle?.vehicle_id === v.vehicle_id;
                  return (
                    <button
                      key={v.vehicle_id}
                      data-testid={`select-vehicle-${v.vehicle_id}`}
                      disabled={!v.available}
                      onClick={() => setSelectedVehicle(v)}
                      className={`w-full text-left border p-4 flex items-center gap-5 transition-all ${sel ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-white/10 hover:border-[#D4AF37]/50'} ${!v.available ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <img src={v.image_url} alt={v.name} className="w-28 h-20 object-cover"/>
                      <div className="flex-1">
                        <div className="font-serif text-xl">{v.name}</div>
                        <div className="text-xs text-[#71717A] mb-2">{v.subtitle}</div>
                        <div className="flex items-center gap-4 text-xs text-[#A1A1AA] uppercase tracking-[0.16em]">
                          <span className="flex items-center gap-1"><Users size={12}/>{v.pax}</span>
                          <span className="flex items-center gap-1"><Briefcase size={12}/>{v.luggage}</span>
                          {!v.available && <span className="text-red-400">capacity exceeded</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-serif text-2xl text-[#D4AF37]" data-testid={`fare-${v.vehicle_id}`}>${displayFare(v).toFixed(2)}</div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-[#71717A]">
                          {effectiveTipPct() === 0
                            ? "no gratuity"
                            : `incl. ${Math.round(effectiveTipPct()*100)}% gratuity`}
                        </div>
                        {sel && <Check size={18} className="text-[#D4AF37] ml-auto mt-2"/>}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-10 flex justify-between gap-4">
                <button data-testid="back-step1" onClick={() => setStep(1)} className="btn-ghost">Back</button>
                <button data-testid="continue-contact" disabled={!selectedVehicle} onClick={() => setStep(3)} className="btn-gold">
                  Continue <ArrowRight size={16}/>
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — Contact */}
          {step === 3 && (
            <div data-testid="step-contact">
              <div className="mb-8">
                <div className="overline mb-2">Contact & confirmation</div>
                <p className="text-sm text-[#A1A1AA]">We&apos;ll send your confirmation by email or SMS — whichever you provide. No payment required to reserve · pay-later.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
                <div className="md:col-span-2">
                  <label className="field-label">Full name</label>
                  <input data-testid="input-name" className="field-input" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe"/>
                </div>
                <div>
                  <label className="field-label">Email <span className="text-[#71717A] normal-case tracking-normal text-[10px] ml-1">(email or phone — one required)</span></label>
                  <input data-testid="input-email" type="email" className="field-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com"/>
                </div>
                <div>
                  <label className="field-label">Mobile phone <span className="text-[#71717A] normal-case tracking-normal text-[10px] ml-1">(email or phone — one required)</span></label>
                  <input data-testid="input-phone" className="field-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (414) 333-6275"/>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm text-[#F5F2EB] cursor-pointer">
                    <input data-testid="input-for-someone-else" type="checkbox" checked={forSomeoneElse}
                      onChange={e => setForSomeoneElse(e.target.checked)} className="accent-[#D4AF37]"/>
                    Booking for someone else?
                    <span className="text-[#71717A] text-[10px] uppercase tracking-[0.14em] ml-1">guest info goes on the trip tracking &amp; chauffeur pages</span>
                  </label>
                  {forSomeoneElse && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <label className="field-label">Guest name(s)</label>
                        <input data-testid="input-guest-name" className="field-input" value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="e.g. Amber and Sara"/>
                      </div>
                      <div>
                        <label className="field-label">Guest phone (optional)</label>
                        <input data-testid="input-guest-phone" className="field-input" value={guestPhone} onChange={e => setGuestPhone(e.target.value)} placeholder="+1 (210) 555-1234"/>
                      </div>
                      <div>
                        <label className="field-label">Number of guests</label>
                        <input data-testid="input-guest-count" type="number" min="1" className="field-input" value={guestCount} onChange={e => setGuestCount(e.target.value)} placeholder="2"/>
                      </div>
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Notes for chauffeur (optional)</label>
                  <textarea data-testid="input-notes" rows={3} className="field-input resize-none" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Flight #, child seat, special requests…"/>
                </div>

                {/* Dispatcher: save the client's card on file while setting them up */}
                {user?.role === "dispatcher" && (
                  <div className="md:col-span-2 border-t border-white/5 pt-6 mt-2" data-testid="dispatcher-card-section">
                    <div className="overline mb-3">Client card on file <span className="text-[#71717A] normal-case tracking-normal text-[10px] ml-1">(dispatcher only · optional)</span></div>
                    {email && email.includes("@") ? (
                      <DispatcherAddCard email={email.trim().toLowerCase()}/>
                    ) : (
                      <p className="text-xs text-[#71717A]">Enter the client&apos;s email above to save a card on their file.</p>
                    )}
                  </div>
                )}

                {/* Corporate billing — only for members of a corporate account */}
                {corpAccounts.length > 0 && (
                  <div className="md:col-span-2 border-t border-white/5 pt-6 mt-2" data-testid="billing-choice-section">
                    <div className="overline mb-3">Bill this trip to</div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid="bill-personal-btn"
                        onClick={() => setBillTo("personal")}
                        className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.16em] border transition ${billTo === "personal" ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10 font-bold" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}
                      >Personal trip</button>
                      {corpAccounts.map(a => (
                        <button
                          key={a.account_id}
                          type="button"
                          data-testid={`bill-corp-${a.account_id}`}
                          onClick={() => setBillTo(a.account_id)}
                          className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.16em] border transition ${billTo === a.account_id ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10 font-bold" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}
                        >Business — {a.name}</button>
                      ))}
                    </div>
                    <p className="text-xs text-[#71717A] mt-2">
                      {billTo === "personal"
                        ? "This ride stays on your personal account — it will not appear on any corporate invoice."
                        : "This ride bills to the company's consolidated invoice."}
                    </p>
                  </div>
                )}

                <div className="md:col-span-2 border-t border-white/5 pt-6 mt-2">
                  <div className="overline mb-3">Promo / coupon code</div>
                  <CouponPicker
                    isPublic={true}
                    customer_email={email}
                    value={couponCode}
                    onChange={(code) => {
                      setCouponCode(code);
                      // Re-quote with the FRESH code (bypass React state race)
                      if (pickup && (dropoff || tripType === "hourly")) {
                        setTimeout(() => getQuote(undefined, { coupon_code: code || null }), 100);
                      }
                    }}
                    dataTestidPrefix="book-coupon"
                  />
                </div>

                <div className="md:col-span-2 border-t border-white/5 pt-6 mt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      data-testid="meet-greet-checkbox"
                      type="checkbox"
                      checked={meetGreet}
                      onChange={e => {
                        setMeetGreet(e.target.checked);
                        // Re-quote so the +$20 line updates immediately
                        if (pickup && (dropoff || tripType === "hourly")) {
                          setTimeout(() => getQuote(undefined, { meet_and_greet: e.target.checked }), 60);
                        }
                      }}
                      className="w-4 h-4 accent-[#D4AF37] mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm uppercase tracking-[0.2em] text-[#F5F2EB]">Meet &amp; Greet at pickup</span>
                        <span className="text-sm text-[#D4AF37] font-medium whitespace-nowrap">+$20</span>
                      </div>
                      <p className="text-xs text-[#71717A] mt-1">Chauffeur will wait inside with a personalized name sign — perfect for airport arrivals, hotels, and out-of-town clients. Skips the curbside shuffle.</p>
                    </div>
                  </label>
                </div>

                {/* Recurring (corporate / weekly) */}
                <div className="md:col-span-2 border-t border-white/5 pt-6 mt-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      data-testid="recurring-checkbox"
                      type="checkbox"
                      checked={recurring}
                      onChange={e => setRecurring(e.target.checked)}
                      className="w-4 h-4 accent-[#D4AF37]"
                    />
                    <span className="text-sm uppercase tracking-[0.2em] text-[#F5F2EB]">Make this a recurring ride</span>
                  </label>
                  <p className="text-xs text-[#71717A] mt-1 ml-7">Perfect for corporate commutes, executive transport, weekly errands.</p>

                  {recurring && (
                    <div className="grid grid-cols-2 gap-4 mt-5 ml-7">
                      <div>
                        <label className="field-label">Frequency</label>
                        <select
                          data-testid="recurring-frequency"
                          value={frequency}
                          onChange={e => setFrequency(e.target.value)}
                          className="field-input bg-black"
                        >
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Every 2 weeks</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      <div>
                        <label className="field-label"># of rides</label>
                        <input
                          data-testid="recurring-occurrences"
                          type="number"
                          min={2}
                          max={52}
                          className="field-input"
                          value={occurrences}
                          onChange={e => setOccurrences(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedVehicle && (
                <div className="mt-10 border-t border-white/10 pt-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-3">Price Breakdown</div>
                  <div className="space-y-1.5 text-sm">
                    {tripType === "point_to_point" ? (
                      <>
                        <FareLine label="Base fare" amt={selectedVehicle.breakdown?.base} />
                        <FareLine label={`Distance (${selectedVehicle.breakdown?.distance_miles} mi × $${selectedVehicle.breakdown?.per_mile?.toFixed(2)})`} amt={selectedVehicle.breakdown?.distance_cost} />
                      </>
                    ) : (
                      <FareLine label={`Hourly (${selectedVehicle.breakdown?.hours} hr × $${selectedVehicle.breakdown?.hourly_rate?.toFixed(2)})`} amt={selectedVehicle.breakdown?.base_subtotal ?? selectedVehicle.breakdown?.subtotal} />
                    )}
                    {selectedVehicle.breakdown?.event_surcharge !== 0 && selectedVehicle.breakdown?.event_surcharge != null && (
                      <FareLine
                        data-testid="fare-line-event-surcharge"
                        label={`${selectedVehicle.breakdown.event_surcharge_label || "Scheduled rate"} (${selectedVehicle.breakdown.event_surcharge_pct > 0 ? "+" : ""}${selectedVehicle.breakdown.event_surcharge_pct}%)`}
                        amt={selectedVehicle.breakdown.event_surcharge}
                        accent={selectedVehicle.breakdown.event_surcharge < 0}
                      />
                    )}
                    {selectedVehicle.breakdown?.multistop_fee > 0 && <FareLine label="Multi-stop fee" amt={selectedVehicle.breakdown.multistop_fee} />}
                    <FareLine label="Subtotal" amt={selectedVehicle.breakdown?.subtotal} muted bold />
                    {selectedVehicle.breakdown?.coupon_discount > 0 && (
                      <FareLine
                        label={`Coupon · ${selectedVehicle.breakdown.coupon_code} (${selectedVehicle.breakdown.coupon_pct}% off)`}
                        amt={-Math.abs(selectedVehicle.breakdown.coupon_discount)}
                        accent
                      />
                    )}
                    {selectedVehicle.breakdown?.airport_fee > 0 && <FareLine label="Airport fee" amt={selectedVehicle.breakdown.airport_fee} />}
                    {selectedVehicle.breakdown?.meet_and_greet_fee > 0 && <FareLine label="Meet & Greet (name board)" amt={selectedVehicle.breakdown.meet_and_greet_fee} />}
                    {selectedVehicle.breakdown?.tolls > 0 && <FareLine label="Toll fee" amt={selectedVehicle.breakdown.tolls} />}
                    <FareLine label="Credit card fee (3.5%)" amt={(() => {
                      const bd = selectedVehicle.breakdown || {};
                      // Use post-coupon subtotal so the fee reflects the discount
                      const sub = bd.subtotal_after_coupon ?? bd.subtotal ?? 0;
                      return +(sub * 0.035).toFixed(2);
                    })()} />
                    <FareLine label="Flexible cancellation" amt={8.0} />
                    <FareLine label={effectiveTipPct() === 0 ? "No tip" : `Driver gratuity (${Math.round(effectiveTipPct()*100)}%)`} amt={tipAmount(selectedVehicle)} />
                    <FareLine label={`State sales tax (${(selectedVehicle.breakdown?.tax_pct*100 || 5.5).toFixed(1)}%)`} amt={(() => {
                      const bd = selectedVehicle.breakdown || {};
                      // Use post-coupon subtotal so tax base reflects the discount
                      const sub = bd.subtotal_after_coupon ?? bd.subtotal ?? 0;
                      const tip = sub * effectiveTipPct();
                      const cc = +(sub * 0.035).toFixed(2);
                      const preTax = sub + tip + (bd.airport_fee||0) + cc + 8.0 + (bd.tolls||0) + (bd.meet_and_greet_fee||0);
                      return +(preTax * (bd.tax_pct || 0.055)).toFixed(2);
                    })()} />
                  </div>
                  {(() => {
                    const rtActive = isRoundTrip && tripType === "point_to_point" && !!returnDt;
                    const outboundTotal = fareAdjustActive ? parseFloat(fareAdjust) : displayFare(selectedVehicle);
                    const retV = rtActive ? (returnQuote?.vehicles || []).find(v => v.vehicle_id === selectedVehicle?.vehicle_id) : null;
                    const returnTotal = retV ? displayFare(retV) : null;
                    const combined = outboundTotal + (returnTotal ?? outboundTotal);
                    return (
                      <>
                        {rtActive && (
                          <div className="mt-3 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-2.5 space-y-1.5" data-testid="step3-roundtrip-hint">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Round trip · both rides</div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-[#A1A1AA]">Outbound · {pickupDt ? new Date(pickupDt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</span>
                              <span className="text-[#F5F2EB]" data-testid="step3-outbound-fare">${outboundTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-[#A1A1AA]">Return · {new Date(returnDt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                              <span className="text-[#F5F2EB]" data-testid="step3-return-fare">{returnTotal != null ? `$${returnTotal.toFixed(2)}` : "pricing…"}</span>
                            </div>
                          </div>
                        )}
                        <div className="flex items-end justify-between mt-4 pt-3 border-t border-white/10">
                          <div>
                            <div className="overline">{rtActive ? "Round trip total" : "Total"}</div>
                            <div className="font-serif text-3xl text-[#D4AF37]" data-testid="step3-total">
                              {rtActive && returnTotal == null ? "≈ " : ""}${(rtActive ? combined : outboundTotal).toFixed(2)}
                            </div>
                            {rtActive && (
                              <div className="text-xs text-[#71717A]" data-testid="step3-roundtrip-note">
                                covers both rides · charged together at checkout
                              </div>
                            )}
                            {fareAdjustActive && (
                              <div className="text-xs text-[#71717A] line-through" data-testid="step3-original-total">
                                ${displayFare(selectedVehicle).toFixed(2)}
                              </div>
                            )}
                          </div>
                          <div className="text-right text-xs text-[#71717A] max-w-xs">
                            {selectedVehicle.name}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                  {user?.role === "dispatcher" && (
                    <div className="mt-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4" data-testid="price-adjust-box">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Dispatch · Adjust price</div>
                      <div className="text-[11px] text-[#71717A] mb-3">
                        Price-match a repeat client — the amount below replaces the calculated total. Leave blank to charge the standard fare.
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[#D4AF37] font-serif text-lg">$</span>
                        <input
                          data-testid="price-adjust-input"
                          type="number" min="1" step="0.01" inputMode="decimal"
                          value={fareAdjust}
                          onChange={e => setFareAdjust(e.target.value)}
                          placeholder={displayFare(selectedVehicle).toFixed(2)}
                          className="w-36 bg-black/40 border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] focus:outline-none focus:border-[#D4AF37]/60"
                        />
                        {fareAdjustActive && (
                          <span className="text-[11px]" data-testid="price-adjust-note">
                            <span className={parseFloat(fareAdjust) < displayFare(selectedVehicle) ? "text-emerald-300" : "text-amber-300"}>
                              {parseFloat(fareAdjust) < displayFare(selectedVehicle)
                                ? `$${(displayFare(selectedVehicle) - parseFloat(fareAdjust)).toFixed(2)} discount applied`
                                : `$${(parseFloat(fareAdjust) - displayFare(selectedVehicle)).toFixed(2)} above the quote`}
                            </span>
                          </span>
                        )}
                        {fareAdjust && (
                          <button data-testid="price-adjust-clear" onClick={() => { setFareAdjust(""); setFareAdjustReason(""); }}
                            className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] hover:text-white ml-auto">Clear</button>
                        )}
                      </div>
                      {fareAdjustActive && (
                        <input
                          data-testid="price-adjust-reason"
                          type="text" maxLength={300}
                          value={fareAdjustReason}
                          onChange={e => setFareAdjustReason(e.target.value)}
                          placeholder="Reason (optional) — e.g. price match competitor quote"
                          className="mt-3 w-full bg-black/40 border border-white/10 px-3 py-2 text-xs text-[#F5F2EB] placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/60"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-10 flex flex-col sm:flex-row sm:justify-between gap-3">
                <button data-testid="back-step2" onClick={() => setStep(2)} className="btn-ghost sm:mr-auto">Back</button>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    data-testid="confirm-pay-later-button"
                    disabled={submitting}
                    onClick={() => { setPayNowFlow(false); submitBooking(); }}
                    className="btn-ghost whitespace-nowrap"
                  >
                    {submitting && !payNowFlow ? "Reserving…" : "Reserve · Pay later"}
                  </button>
                  <button
                    data-testid="confirm-pay-now-button"
                    disabled={submitting}
                    onClick={() => { setPayNowFlow(true); submitBooking(); }}
                    className="btn-gold whitespace-nowrap"
                  >
                    {submitting && payNowFlow ? "Reserving…" : "Reserve & Pay now"}
                  </button>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-[#71717A] text-right">
                Pay now to lock in your reservation — Card · Apple Pay · Google Pay · Zelle · Venmo. Or pay later from the confirmation email.
              </p>
            </div>
          )}

          {/* STEP 4 — Confirmation */}
          {step === 4 && confirmation && (
            <div data-testid="step-confirmed" className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-6 border-2 border-[#D4AF37] flex items-center justify-center">
                <Check size={28} className="text-[#D4AF37]"/>
              </div>
              <div className="overline mb-3">Reservation confirmed</div>
              <h2 className="font-serif text-4xl mb-2">Thank you, {confirmation.customer_name.split(" ")[0]}.</h2>
              <div className="font-serif text-2xl text-[#D4AF37] mb-3">{confirmation.confirmation_code}</div>
              {confirmation.series_id && (
                <div data-testid="series-banner" className="inline-block mb-6 border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-5 py-3">
                  <div className="overline mb-1">Recurring series</div>
                  <div className="text-sm text-[#F5F2EB]">{confirmation.occurrences} {confirmation.frequency} rides · series total <span className="text-[#D4AF37]">${confirmation.total_series.toFixed(2)}</span></div>
                </div>
              )}
              <p className="text-[#A1A1AA] max-w-md mx-auto mb-10 text-sm leading-relaxed">
                A confirmation has been sent to <span className="text-[#F5F2EB]">{confirmation.customer_email}</span> and a text to <span className="text-[#F5F2EB]">{confirmation.customer_phone}</span>. Our dispatcher will assign your chauffeur shortly.
              </p>

              <div className="text-left max-w-md mx-auto border border-white/10 p-6 mb-8">
                <SummaryRow label="Vehicle" value={confirmation.vehicle_name}/>
                <SummaryRow label="Pickup" value={new Date(confirmation.pickup_datetime).toLocaleString()}/>
                <SummaryRow label="From" value={confirmation.pickup_address}/>
                {confirmation.trip_type === "point_to_point" ? (
                  <>
                    <SummaryRow label="To" value={confirmation.dropoff_address}/>
                    <SummaryRow label="Distance" value={`${confirmation.distance_miles} mi`}/>
                  </>
                ) : (
                  <SummaryRow label="Hours" value={`${confirmation.hours} hr`}/>
                )}
                <SummaryRow label="Passengers" value={`${confirmation.pax} pax · ${confirmation.luggage} bags`}/>
                {confirmation.flight_number && <SummaryRow label="Flight" value={confirmation.flight_number}/>}
                {confirmation.return_leg ? (
                  <>
                    <SummaryRow label="Return pickup" value={new Date(confirmation.return_leg.pickup_datetime).toLocaleString()}/>
                    <SummaryRow label="Outbound fare" value={`$${confirmation.fare.toFixed(2)}`}/>
                    <SummaryRow label="Return fare" value={`$${Number(confirmation.return_leg.fare).toFixed(2)}`}/>
                    <SummaryRow
                      label={confirmation.payment_status === "paid" ? "Round trip total · Paid" : "Round trip total"}
                      value={`$${(confirmation.fare + Number(confirmation.return_leg.fare)).toFixed(2)}`}
                      highlight
                    />
                  </>
                ) : (
                  <SummaryRow label={confirmation.payment_status === "paid" ? "Total · Paid" : "Total"} value={`$${confirmation.fare.toFixed(2)}`} highlight/>
                )}
                {confirmation.payment_method && <SummaryRow label="Payment" value={confirmation.payment_method.replace("_", " ").toUpperCase()}/>}
              </div>

              {/* Payment Selector — only show if not yet paid */}
              {confirmation.payment_status !== "paid" && confirmation.payment_method !== "cash" && confirmation.payment_method !== "venmo" && confirmation.payment_method !== "zelle" && (
                <div
                  className={`max-w-md mx-auto mb-10 text-left ${payNowFlow ? "border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5" : ""}`}
                  data-testid="step4-payment-selector"
                  ref={(node) => { if (payNowFlow && node) node.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                >
                  {payNowFlow && (
                    <div className="mb-3 overline text-[#D4AF37]" data-testid="pay-now-banner">
                      Complete payment to lock in your reservation
                    </div>
                  )}
                  <PaymentSelector booking={confirmation} onPaid={setConfirmation}/>
                </div>
              )}

              <div className="flex flex-wrap justify-center gap-4">
                <button data-testid="book-another" className="btn-ghost" onClick={() => { setStep(1); setConfirmation(null); setSelectedVehicle(null); setQuote(null); setPayNowFlow(false); }}>Book another</button>
                <a
                  data-testid="track-ride-link"
                  href={`/track?code=${confirmation.confirmation_code}&email=${encodeURIComponent(confirmation.customer_email)}`}
                  className="btn-ghost"
                >Track ride</a>
                <a
                  data-testid="download-trip-pdf"
                  href={`${API}/bookings/${confirmation.confirmation_code}/trip-sheet.pdf?contact=${encodeURIComponent(confirmation.customer_email || confirmation.customer_phone || "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                >Download Trip Sheet PDF</a>
                {confirmation.payment_status === "paid" && (
                  <a
                    data-testid="download-receipt-pdf"
                    href={`${API}/bookings/${confirmation.confirmation_code}/receipt.pdf?contact=${encodeURIComponent(confirmation.customer_email || confirmation.customer_phone || "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost"
                  >Download Receipt PDF</a>
                )}
                {user ? (
                  <button data-testid="view-trips" className="btn-gold" onClick={() => nav("/customer")}>My Trips</button>
                ) : (
                  <button data-testid="home-cta" className="btn-gold" onClick={() => nav("/")}>Done</button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer/>
    </div>
  );
}

function FareLine({ label, amt, muted, bold, accent }) {
  const sign = amt < 0 ? "−" : "";
  return (
    <div className={`flex justify-between ${bold ? "border-t border-white/10 pt-2 mt-1" : ""}`}>
      <span className={accent ? "text-[#D4AF37]" : muted ? "text-[#A1A1AA]" : "text-[#71717A]"}>{label}</span>
      <span className={accent ? "text-[#D4AF37]" : bold ? "text-[#F5F2EB] font-medium" : "text-[#F5F2EB]"}>
        {sign}${Math.abs(amt || 0).toFixed(2)}
      </span>
    </div>
  );
}

function Stepper({ step, onJump }) {
  const items = ["Trip", "Vehicle", "Contact", "Confirmed"];
  return (
    <div className="flex items-center gap-3 mb-4">
      {items.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        const canJump = done && step !== 4 && onJump;
        const Cmp = canJump ? "button" : "div";
        return (
          <React.Fragment key={label}>
            <Cmp
              type={canJump ? "button" : undefined}
              onClick={canJump ? () => onJump(n) : undefined}
              data-testid={canJump ? `stepper-jump-${n}` : undefined}
              className={`flex items-center gap-3 ${active ? 'text-[#D4AF37]' : done ? 'text-[#F5F2EB]' : 'text-[#52525B]'} ${canJump ? 'cursor-pointer hover:text-[#D4AF37] transition-colors' : ''}`}
            >
              <div className={`w-7 h-7 border flex items-center justify-center text-xs ${active ? 'border-[#D4AF37]' : done ? 'border-[#F5F2EB]' : 'border-[#27272A]'}`}>{done ? <Check size={12}/> : n}</div>
              <span className="text-[11px] uppercase tracking-[0.2em] hidden sm:inline">{label}</span>
            </Cmp>
            {n < items.length && <div className="h-px flex-1 bg-white/10"/>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function SummaryRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-baseline py-2 border-b border-white/5 last:border-b-0 gap-4">
      <span className="text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] shrink-0">{label}</span>
      <span className={`text-sm text-right ${highlight ? 'text-[#D4AF37] font-serif text-lg' : 'text-[#F5F2EB]'}`}>{value}</span>
    </div>
  );
}
